import { Alert, Box, Button, Chip, Stack } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  storageKey, useStorageAction, type EventFilters, type StorageAdmin, type StorageEvent,
} from '@/api/storage-admin';
import { formatBytes, FormFields, type FieldDef, type FieldOption } from '@/components/fields';
import { LocalDataTable } from '@/components/grid/LocalDataTable';
import { MODULE_LABELS, serverError } from './common';

const EVENTS: Record<string, string> = {
  INIT: 'Upload started', COMPLETE: 'Upload finished', UPLOAD_LOCAL: 'Saved on this server', DEDUP: 'Duplicate reused',
  ATTACH: 'Attached to a record', DOWNLOAD: 'Opened', DELETE: 'Deleted', PURGE: 'Removed for good',
  SYNC: 'Sent to S3', QUOTA: 'Quota', BREAKER: 'S3 paused / resumed', HEALTH: 'Server check',
  RELOCATE: 'Moving files', BACKFILL: 'Older file adopted', CONFIG: 'Settings changed', RESTORE: 'Restored',
  INTEGRITY: 'Integrity check',
};
const asOptions = (labels: Record<string, string>): FieldOption[] =>
  Object.entries(labels).map(([value, label]) => ({ value, label }));

const FILTER_FIELDS: FieldDef[] = [
  { type: 'select', name: 'event', label: 'What happened', options: asOptions(EVENTS), config: { emptyLabel: 'Anything' },
    grid: { xs: 12, md: 4 } },
  { type: 'select', name: 'outcome', label: 'Result', config: { emptyLabel: 'Any' }, grid: { xs: 12, md: 4 },
    options: [{ value: 'OK', label: 'Done' }, { value: 'FAIL', label: 'Failed' }, { value: 'DENIED', label: 'Refused' }] },
  { type: 'select', name: 'module', label: 'Where', options: asOptions(MODULE_LABELS), config: { emptyLabel: 'Anywhere' },
    grid: { xs: 12, md: 4 } },
];

const OUTCOME_COLOR = { OK: 'success', FAIL: 'error', DENIED: 'warning' } as const;

/** Every upload, download, sync and settings change, newest first. */
export function LogsSection({ admin }: { admin: StorageAdmin }) {
  const [filters, setFilters] = useState<EventFilters>({});
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const events = useInfiniteQuery({
    queryKey: storageKey(admin, 'events', filters),
    queryFn: ({ pageParam }) => admin.events!({ ...filters, before_id: pageParam }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (page) => page.next_before_id ?? undefined,
  });
  const retry = useStorageAction(admin, (fileId: string) => admin.retry!(fileId));
  const rows = events.data?.pages.flatMap((p) => p.items) ?? [];

  const columns: GridColDef<StorageEvent>[] = [
    { field: 'at', headerName: 'When', minWidth: 170, valueFormatter: (v: string) => new Date(v).toLocaleString('en-IN') },
    { field: 'event', headerName: 'What happened', minWidth: 170, valueFormatter: (v: string) => EVENTS[v] ?? v },
    {
      field: 'outcome', headerName: 'Result', minWidth: 100,
      renderCell: ({ row }) => <Chip size="small" variant="outlined" color={OUTCOME_COLOR[row.outcome]} label={row.outcome} />,
    },
    {
      field: 'module', headerName: 'Where', minWidth: 160,
      valueFormatter: (v: string | null) => (v ? MODULE_LABELS[v as keyof typeof MODULE_LABELS] ?? v : '—'),
    },
    { field: 'bytes', headerName: 'Size', type: 'number', minWidth: 90, valueFormatter: (v: number | null) => (v ? formatBytes(v) : '—') },
    { field: 'driver', headerName: 'On', minWidth: 70, valueFormatter: (v: string | null) => v ?? '—' },
    {
      field: 'message', headerName: 'Details', minWidth: 260, flex: 1,
      valueGetter: (_v, r) => [r.error_code, r.message].filter(Boolean).join(' · ') || '—',
    },
    {
      field: 'actions', headerName: '', width: 90, sortable: false,
      renderCell: ({ row }) => row.event === 'SYNC' && row.outcome === 'FAIL' && row.file_id && (
        <Button size="small" disabled={retry.isPending} onClick={() => {
          setError(undefined);
          retry.mutateAsync(row.file_id!).then(
            () => setNotice('The file will be sent to S3 on the next worker pass.'),
            (e) => setError(serverError(e).message),
          );
        }}>Retry</Button>
      ),
    },
  ];

  return (
    <Stack spacing={2}>
      <FormFields fields={FILTER_FIELDS} values={filters as Record<string, unknown>}
                  onChange={(name, value) => setFilters((f) => ({ ...f, [name]: value ?? undefined }))} />
      {(events.error || error) && (
        <Alert severity="error" onClose={() => setError(undefined)}>{error ?? serverError(events.error).message}</Alert>
      )}
      {notice && <Alert severity="success" onClose={() => setNotice(undefined)}>{notice}</Alert>}
      <LocalDataTable
        rows={rows} columns={columns} getRowId={(r) => r.id} loading={events.isLoading} wrap
        searchText={(r) => `${r.message ?? ''} ${r.error_code ?? ''} ${r.file_id ?? ''} ${r.request_id ?? ''}`}
        searchPlaceholder="Search messages, file or request id" noun={['event', 'events']}
        emptyMessage="Nothing logged for these filters."
      />
      {events.hasNextPage && (
        <Box>
          <Button onClick={() => events.fetchNextPage()} disabled={events.isFetchingNextPage}>
            {events.isFetchingNextPage ? 'Loading…' : 'Load older events'}
          </Button>
        </Box>
      )}
    </Stack>
  );
}
